import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { NgClass } from '@angular/common';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';

import { ClientRegistrationRecord } from '../../services/registration-form.service';
import { ClientRegistrationManagementService } from '../../services/client-registration-management.service';

type RegistrationFilterOption = 'ALL' | 'QCA_REGISTRATION' | 'UTILITY_REGISTRATION' | 'GENERATING_COMPANY' | 'PSS_OWNER';
type RegistrationStatusOption = 'ALL' | 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'DRAFT';

@Component({
  selector: 'app-manage-registration',
  standalone: true,
  imports: [ReactiveFormsModule, NgClass],
  templateUrl: './manage-registration.component.html',
  styleUrls: ['./manage-registration.component.scss']
})
export class ManageRegistrationComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly registrationService = inject(ClientRegistrationManagementService);

  readonly registrations = signal<ClientRegistrationRecord[]>([]);
  readonly searchTerm = signal('');
  readonly currentPage = signal(1);
  readonly pageSize = signal(10);
  readonly toast = signal<{ message: string; type: 'success' | 'error' } | null>(null);
  readonly selectedRowIds = signal<number[]>([]);
  readonly selectedRegistration = signal<ClientRegistrationRecord | null>(null);
  readonly reviewRemarks = signal('');
  readonly isReviewBusy = signal(false);
  readonly openActionMenuId = signal<number | null>(null);
  readonly actionMenuPosition = signal({ top: 0, left: 0 });

  readonly filterForm = this.fb.nonNullable.group({
    entity_type: ['ALL' as RegistrationFilterOption],
    status: ['ALL' as RegistrationStatusOption]
  });

  readonly filteredRegistrations = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    const entityType = this.filterForm.controls.entity_type.value;
    const status = this.filterForm.controls.status.value;

    return this.registrations().filter((item) => {
      const matchesTerm = !term || [item.registration_no, item.entity_name, item.username, item.email, item.mobile || '']
        .some((value) => value.toLowerCase().includes(term));
      const matchesEntity = entityType === 'ALL' || item.entity_type === entityType;
      const matchesStatus = status === 'ALL' || item.status === status;
      return matchesTerm && matchesEntity && matchesStatus;
    });
  });

  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.filteredRegistrations().length / this.pageSize())));
  readonly pageNumbers = computed(() => Array.from({ length: this.totalPages() }, (_, index) => index + 1));
  readonly paginatedRegistrations = computed(() => {
    const page = Math.min(this.currentPage(), this.totalPages());
    const start = (page - 1) * this.pageSize();
    return this.filteredRegistrations().slice(start, start + this.pageSize());
  });

  ngOnInit(): void {
    this.load();
  }

  onSearch(value: string): void {
    this.searchTerm.set(value);
    this.currentPage.set(1);
  }

  onFilterChange(): void {
    this.currentPage.set(1);
  }

  onPageSizeChange(value: string): void {
    this.pageSize.set(Number(value) || 10);
    this.currentPage.set(1);
  }

  toggleRow(id: number, checked: boolean): void {
    const next = new Set(this.selectedRowIds());
    if (checked) {
      next.add(id);
    } else {
      next.delete(id);
    }
    this.selectedRowIds.set([...next]);
  }

  isRowSelected(id: number): boolean {
    return this.selectedRowIds().includes(id);
  }

  openRegistration(registration: ClientRegistrationRecord): void {
    this.selectedRegistration.set(registration);
    this.reviewRemarks.set('');
    this.openActionMenuId.set(null);
  }

  closeRegistration(): void {
    this.selectedRegistration.set(null);
    this.reviewRemarks.set('');
    this.isReviewBusy.set(false);
  }

  goToPage(page: number): void {
    this.currentPage.set(Math.min(Math.max(page, 1), this.totalPages()));
  }

  nextPage(): void {
    this.goToPage(this.currentPage() + 1);
  }

  prevPage(): void {
    this.goToPage(this.currentPage() - 1);
  }

  openRowActions(id: number, event: MouseEvent): void {
    const button = event.currentTarget as HTMLElement;
    const rect = button.getBoundingClientRect();
    this.actionMenuPosition.set({
      top: rect.bottom + 6,
      left: Math.max(12, rect.right - 164)
    });
    this.openActionMenuId.set(this.openActionMenuId() === id ? null : id);
  }

  approveSelected(): void {
    this.reviewSelected('APPROVE');
  }

  rejectSelected(): void {
    this.reviewSelected('REJECT');
  }

  formatDateTime(value: string | null): string {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? '-'
      : new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(date);
  }

  private load(): void {
    this.registrationService.list().subscribe({
      next: (response) => {
        this.registrations.set(response.value === '' ? [] : response.value);
      },
      error: (error: HttpErrorResponse) => {
        this.toast.set({ message: error.error?.message || 'Unable to load registrations.', type: 'error' });
      }
    });
  }

  private reviewSelected(action: 'APPROVE' | 'REJECT'): void {
    const registration = this.selectedRegistration();
    if (!registration || this.isReviewBusy()) {
      return;
    }

    this.isReviewBusy.set(true);
    this.registrationService.review(registration.id, {
      action,
      remarks: this.reviewRemarks().trim() || null
    }).subscribe({
      next: (response) => {
        this.toast.set({ message: response.message, type: 'success' });
        this.closeRegistration();
        this.load();
      },
      error: (error: HttpErrorResponse) => {
        this.toast.set({ message: error.error?.message || 'Unable to process registration review.', type: 'error' });
        this.isReviewBusy.set(false);
      }
    });
  }

  statusLabel(status: string): string {
    switch (status) {
      case 'APPROVED':
        return 'Approved';
      case 'REJECTED':
        return 'Rejected';
      case 'DRAFT':
        return 'Draft';
      default:
        return 'Submitted';
    }
  }

  statusClass(status: string): string {
    switch (status) {
      case 'APPROVED':
        return 'approved';
      case 'REJECTED':
        return 'rejected';
      case 'DRAFT':
        return 'draft';
      default:
        return 'submitted';
    }
  }

  payloadEntries(registration: ClientRegistrationRecord): Array<[string, unknown]> {
    return Object.entries(registration.payload_json || {});
  }

  labelFromKey(key: string): string {
    return key
      .split('_')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  payloadValue(registration: ClientRegistrationRecord | null, key: string): unknown {
    return registration?.payload_json?.[key];
  }

  hasPayloadValue(registration: ClientRegistrationRecord | null, key: string): boolean {
    const value = this.payloadValue(registration, key);
    return value !== null && value !== undefined && value !== '';
  }

  formatValue(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  }

  formatListValue(value: unknown): string {
    if (Array.isArray(value)) {
      return value.map((item) => this.formatValue(item)).join(', ');
    }
    return this.formatValue(value);
  }

  documentUrl(fileType: string, base64: string): string {
    return `data:${fileType};base64,${base64}`;
  }

  entityLabel(entityType: string): string {
    switch (entityType) {
      case 'QCA_REGISTRATION':
        return 'QCA';
      case 'UTILITY_REGISTRATION':
        return 'Utility';
      case 'GENERATING_COMPANY':
        return 'Generation Company';
      case 'PSS_OWNER':
        return 'PSS Owner';
      default:
        return entityType;
    }
  }

  isQcaRegistration(registration: ClientRegistrationRecord | null): boolean {
    return registration?.entity_type === 'QCA_REGISTRATION';
  }

  isPssRegistration(registration: ClientRegistrationRecord | null): boolean {
    return registration?.entity_type === 'PSS_OWNER';
  }
}
